<script setup lang="ts">
import google from "/images/svgs/google-icon.svg";
import github from "/images/svgs/github-mark.svg";
import githubWhite from "/images/svgs/github-mark-white.svg";
import {useAuthentication} from "~/stores/authentication";
import {storeToRefs} from "pinia";
import {useCustomizerStore} from "~/stores/customizer";
import {
  AdjustmentsCodeIcon,
  CheckIcon,
  CreditCardIcon,
  DashboardIcon,
  DatabaseIcon,
  HeartRateMonitorIcon,
  PlayerPlayIcon,
  RouterIcon,
  ScaleIcon
} from "vue-tabler-icons";
import processorsImg from "assets/img/teasers/metrics-aggregate.png";
import serverImg from "assets/img/teasers/server-topology.png";
import flexiblePaymentsImg from "assets/img/teasers/flexible-payments.png";
import {useDisplay} from "vuetify";

const {login, signup} = useAuthentication()
const {authError} = storeToRefs(useAuthentication())
const {darkMode} = storeToRefs(useCustomizerStore());

const {mobile} = useDisplay()

const githubIcon = computed(() => {
  if (darkMode.value) {
    return githubWhite
  } else {
    return github
  }
})
</script>

<template>
  <LayoutDrawer :width="400">
    <div class="px-4 flex-grow-1 d-flex flex-column justify-center">
      <div class="d-flex flex-column justify-center align-center">
        <div style="height: 40px; width: 240px" class="mb-8">
          <LogoConsole></LogoConsole>
        </div>
        <v-alert v-if="authError" color="error" class="my-4">
          <p>We were unable to log you in. We received the following error:</p>
          <p class="my-2">{{ authError }}</p>
          <p>Please try again, or contact <a href="https://www.axoniq.io/support-offerings" target="_blank">AxonIQ
            Support</a></p>
        </v-alert>

        <v-btn @click="signup" color="primary" variant="elevated" elevation="2" size="large" class="w-75">
          Sign up
        </v-btn>

        <div class="my-4 mt-8">
          <v-divider>Or sign in with</v-divider>
        </div>

        <div class="d-flex gap-2">
          <v-btn @click="login('google-oauth2')" variant="outlined" class="border text-subtitle-1 flex-grow-1">
            <img :src="google" height="20" alt="google"/>
          </v-btn>

          <v-btn @click="login('github')" variant="outlined" class="border text-subtitle-1 flex-grow-1">
            <img :src="githubIcon" height="20" alt="Github"/>
          </v-btn>

          <v-btn @click="login('Username-Password-Authentication')" variant="outlined"
                 class="border text-subtitle-1 flex-grow-1 align-center">
            <KeyboardIcon :src="github" size="20" alt="Github"/>
          </v-btn>
        </div>
      </div>

    </div>
  </LayoutDrawer>


  <RouteParamDialog
      width="1300"
      param="details"
      param-value="plans"
      title="AxonIQ Console subscriptions"
      show-close>
    <SubscriptionOptions hide-ctas/>
  </RouteParamDialog>

  <v-main v-if="!mobile">
    <div class="d-flex justify-center align-center flex-grow-1 flex-shrink-1 h-100 tower-background">
      <div class="fixed-width">
        <v-card elevation="1" class="h-100 pa-0">
          <v-card-text class="justify-space-between pa-0 pt-8 h-100">
            <v-carousel continuous color="primary" class="carousel" cycle interval="12000">
              <v-carousel-item>
                <div class="px-16 d-flex flex-column gap-2">
                  <div class="d-flex gap-4 align-center">
                    <div class="h1">
                      Focus only on the business logic
                    </div>
                  </div>
                  <v-divider/>

                  <v-row>
                    <v-col class="pt-8">

                      <p>
                        With Axon Server you don't have to spend time on building infrastructure.
                      </p>
                      <v-list lines="two" class="mt-1">
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="RouterIcon"></v-icon>
                          </template>
                          <v-list-item-title>Zero-configuration message routing</v-list-item-title>
                          <v-list-item-subtitle>
                            Messages are intelligently routed to the correct destination
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="DatabaseIcon"></v-icon>
                          </template>
                          <v-list-item-title>Optimized Event-Souring database</v-list-item-title>
                          <v-list-item-subtitle>Reap the benefits of Event-Sourcing without the downsides
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="HeartRateMonitorIcon"></v-icon>
                          </template>
                          <v-list-item-title>Highly available with clustering</v-list-item-title>
                          <v-list-item-subtitle>
                            Increase reliability of your infrastructure and sleep better
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>

                      <v-alert color="grey" class="mt-1">
                        <p>
                          Our free Developer plan includes a free Axon Server instance for up to 10 applications!
                        </p>
                        <div class="d-flex justify-end">
                          <v-btn color="primary" variant="text" size="small" to="?details=plans" :active="false">See all
                            our
                            subscriptions
                          </v-btn>
                        </div>
                      </v-alert>
                    </v-col>
                    <v-col align-self="center">
                      <v-img :src="serverImg" alt="AxonIQ Console Processors" eager/>
                    </v-col>
                  </v-row>
                </div>
              </v-carousel-item>
              <v-carousel-item>
                <div class="px-16 d-flex flex-column gap-2">
                  <div class="d-flex gap-4 align-center">
                    <div class="h1">
                      Gain deep insights into Axon Framework applications
                    </div>
                  </div>
                  <v-divider/>

                  <v-row class="h-100">
                    <v-col class="pt-8">
                      <p>
                        AxonIQ Console will make sure you have all the insights and tools you need to keep applications
                        running
                        smoothly.
                      </p>
                      <v-list lines="two" class="mt-1">
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="DashboardIcon"></v-icon>
                          </template>
                          <v-list-item-title>Advanced metrics for all components</v-list-item-title>
                          <v-list-item-subtitle>
                            Monitor your applications and processors in real-time
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="PlayerPlayIcon"></v-icon>
                          </template>
                          <v-list-item-title>Full control over your event processors</v-list-item-title>
                          <v-list-item-subtitle>Pause, start, split, merge and replay your processors to your heart's
                            content
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="HeartRateMonitorIcon"></v-icon>
                          </template>
                          <v-list-item-title>Automated monitoring and alerting</v-list-item-title>
                          <v-list-item-subtitle>
                            Get automated notifications when your application is in trouble
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="ScaleIcon"></v-icon>
                          </template>
                          <v-list-item-title>Scale your event processors automatically</v-list-item-title>
                          <v-list-item-subtitle>
                            AxonIQ Console can scale your processors' segments automatically
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-col>
                    <v-col align-self="center">
                      <v-img :src="processorsImg" alt="AxonIQ Console Processors" class="my-2" eager/>
                    </v-col>
                  </v-row>
                </div>
              </v-carousel-item>

              <v-carousel-item>
                <div class="px-16 d-flex flex-column gap-2">
                  <div class="d-flex gap-4 align-center">
                    <div class="h1">
                      Flexibility with no strings attached
                    </div>
                  </div>
                  <v-divider/>

                  <v-row class="h-100">
                    <v-col class="pt-8">
                      <p>
                        All AxonIQ Console subscriptions are flexible and can be changed or ended whenever you want.
                      </p>
                      <v-list lines="two" class="mt-1">
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="CreditCardIcon"></v-icon>
                          </template>
                          <v-list-item-title>Pay monthly using a credit card</v-list-item-title>
                          <v-list-item-subtitle>
                            No need to talk to a salesperson, just pay and go
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="CheckIcon"></v-icon>
                          </template>
                          <v-list-item-title>Only pay for what you need</v-list-item-title>
                          <v-list-item-subtitle>
                            Our subscriptions can be configured between 5 and 100 connections freely
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="AdjustmentsCodeIcon"></v-icon>
                          </template>
                          <v-list-item-title>Change what you need whenever you want</v-list-item-title>
                          <v-list-item-subtitle>
                            You can change the amount of connections in your subscription twice a day
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <v-icon :icon="HeartRateMonitorIcon"></v-icon>
                          </template>
                          <v-list-item-title>No money lost on cancellation or downgrades</v-list-item-title>
                          <v-list-item-subtitle>
                            Downgrades or cancellations will provide credits, which can be used for any future payments
                          </v-list-item-subtitle>
                        </v-list-item>
                        <div class="d-flex justify-end">
                          <v-btn color="primary" variant="text" size="small" to="?details=plans" :active="false">See all
                            our
                            subscriptions
                          </v-btn>
                        </div>
                      </v-list>
                    </v-col>
                    <v-col align-self="center">
                      <v-img :src="flexiblePaymentsImg" alt="AxonIQ Console Processors" class="my-2" eager
                             max-height="350"/>
                    </v-col>
                  </v-row>
                </div>
              </v-carousel-item>


              <v-carousel-item>
                <div class="px-16 d-flex flex-column gap-2">
                  <div class="d-flex gap-4 align-center">
                    <div class="h1">
                      Get started now
                    </div>
                  </div>
                  <v-divider/>

                  <v-row class="h-100">
                    <v-col class="pt-8">
                      <p>
                        The following is always included for free:
                      </p>
                      <v-list lines="two" class="mt-1">
                        <v-list-item>
                          <template v-slot:prepend>
                            <LogoServer :size="20" class="mr-8"/>
                          </template>
                          <v-list-item-title>Axon Server</v-list-item-title>
                          <v-list-item-subtitle>
                            Get Axon Server for up to 10 connections at no charge
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <LogoConsoleIcon :size="20" class="mr-8"/>
                          </template>
                          <v-list-item-title>Dashboards and monitoring</v-list-item-title>
                          <v-list-item-subtitle>
                            You can monitor up to 10 applications for free
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item>
                          <template v-slot:prepend>
                            <LogoFramework :size="20" class="mr-8"/>
                          </template>
                          <v-list-item-title>Axon Framework management</v-list-item-title>
                          <v-list-item-subtitle>
                            Use our tools, so you don't have to build them yourself
                          </v-list-item-subtitle>
                        </v-list-item>

                        <div class="d-flex justify-space-between mt-8">
                          <v-btn color="primary" variant="text" size="small" to="?details=plans" :active="false">See all
                            our
                            subscriptions
                          </v-btn>

                          <v-btn @click="signup" color="primary">
                            Get started
                          </v-btn>
                        </div>
                      </v-list>
                    </v-col>
                    <v-col align-self="center">
                      <v-img :src="processorsImg" alt="AxonIQ Console Processors" class="my-2" eager/>
                    </v-col>
                  </v-row>
                </div>
              </v-carousel-item>

            </v-carousel>
          </v-card-text>
        </v-card>
      </div>
    </div>

  </v-main>
</template>
<style scoped>


.transparent-card {
  opacity: 0.95;

  @media (max-height: 1000px) {
    overflow-y: scroll
  }
}

.carousel {
  --v-theme-surface-variant: rgba(0, 0, 0, 0.9);
}
</style>